import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as FiltersIcon } from '../../../assets/images/filters.svg';
import EmptyDisputesIcon from '../../../assets/svgComponents/EmptyDisputesIcon';
import GlobalFilter from '../../../components/GlobalFilter/GlobalFilter';
import ReactTable from '../../../components/ReactTable/ReactTable';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../../constants/paginationDefaults';
import { getDisputes, postDeclineRequestClose, postDisputeClose } from '../../../redux/actions/disputeActions';
import CloseDispute from '../CloseDispute/CloseDispute';
import DeclineDisputeCloseModal from '../DeclineDisputeClose/DeclineDisputeClose';
import PrePageFilters from '../PrePageFilters/PrePageFilters';

import DisputeFilters from './DisputeFilters/DisputeFilters';
import getDisputeColumns from './DisputeColumns';

import styles from './DisputesTable.module.scss';

const getEmptyTitle = (t) => t('payments.noDisputes');
const DISPUTE_PAGE_FILTERS = ['active', 'resolved', 'all'];

const DisputesTable = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState(null);
  const [filtersDrawer, setFiltersDrawer] = useState(false);
  const [closeDisputeModal, setCloseDisputeModal] = useState({ show: false, data: {} });
  const [declineRequestModal, setDeclineRequestModal] = useState({ show: false, id: null });
  const [disputePageFilter, setDisputePageFilter] = useState('active');

  const disputeFilters = useSelector((state) => state.filtersReducer.disputeFilters);

  const fetchDisputes = (pageArg, perPageArg, searchArg, filtersArg, pageFilter) => {
    setPage(pageArg);
    const formattedFilters = {
      ...filtersArg,
      company_names: filtersArg.company_names.map((obj) => obj.value),
      candidate_names: filtersArg.candidate_names.map((obj) => obj.value),
      experiential_rewards: filtersArg.experiential_rewards.map((obj) => obj.value),
      openings: filtersArg.openings.map((obj) => obj.value),
    };
    dispatch(
      getDisputes(pageArg, perPageArg, searchArg, {
        ...formattedFilters,
        status: pageFilter || disputePageFilter,
      })
    );
  };

  useEffect(() => {
    fetchDisputes(page, perPage, searchTerm, disputeFilters);
  }, [page, perPage]);

  const toggleFilters = useCallback(() => {
    setFiltersDrawer((prevState) => !prevState);
  }, []);

  const disputes = useSelector((state) => state.disputesReducer.disputes);
  const disputesLoading = useSelector((state) => state.disputesReducer.disputesLoading);
  const pageInfo = useSelector((state) => state.disputesReducer.pageInfo);

  const toggleCloseDisputeModal = useCallback((row = {}) => {
    setCloseDisputeModal((prevState) => ({ show: !prevState.show, data: row }));
  }, []);

  const toggleDeclineRequestModal = useCallback((id) => {
    setDeclineRequestModal((prevState) => ({ show: !prevState.show, id }));
  }, []);

  const disputeColumns = useMemo(
    () => getDisputeColumns(toggleCloseDisputeModal, toggleDeclineRequestModal, t),
    [toggleCloseDisputeModal, toggleDeclineRequestModal, t]
  );

  const HEADERS_ACCESSORS = useMemo(() => disputeColumns.map((column) => column.accessor), [disputeColumns]);

  const nonResizableColumns = useMemo(
    () => (disputes.length === 0 ? HEADERS_ACCESSORS : ['open']),
    [disputes.length, HEADERS_ACCESSORS]
  );

  const handleSearch = useCallback(
    (query) => {
      setSearchTerm(query);
      fetchDisputes(DEFAULT_PAGE, perPage, query, disputeFilters);
    },
    [perPage, disputeFilters, disputePageFilter]
  );

  const handleDisputeClose = useCallback((id) => {
    dispatch(postDisputeClose(id, toggleCloseDisputeModal));
  }, []);

  const handleDeclineRequestClose = useCallback(
    (data) => {
      dispatch(postDeclineRequestClose({ ...data, disputeId: declineRequestModal.id }, toggleDeclineRequestModal));
    },
    [toggleDeclineRequestModal, declineRequestModal]
  );

  const handlePageFiltersChange = useCallback((newFilter) => {
    setDisputePageFilter(newFilter);
    fetchDisputes(DEFAULT_PAGE, DEFAULT_PER_PAGE, searchTerm, disputeFilters, newFilter);
  });

  return (
    <div>
      <CloseDispute
        show={closeDisputeModal.show}
        onHide={toggleCloseDisputeModal}
        data={closeDisputeModal.data}
        handleSubmit={handleDisputeClose}
      />
      <DeclineDisputeCloseModal
        show={declineRequestModal.show}
        onHide={toggleDeclineRequestModal}
        data={declineRequestModal.data}
        handleSubmit={handleDeclineRequestClose}
      />
      <DisputeFilters
        toggleFilters={toggleFilters}
        filtersDrawer={filtersDrawer}
        getFilteredDisputes={(updatedFilters) => fetchDisputes(DEFAULT_PAGE, perPage, searchTerm, updatedFilters)}
      />
      <div className={styles.searchfilterCont}>
        <GlobalFilter
          placeholder={t('placeholders.searchDisputes')}
          searchTerm={searchTerm}
          setSearchTerm={handleSearch}
          setPage={setPage}
        />
        <div onClick={toggleFilters} role="presentation" className={styles.filtersCont}>
          <FiltersIcon />
        </div>
      </div>
      <PrePageFilters filters={DISPUTE_PAGE_FILTERS} state={disputePageFilter} setState={handlePageFiltersChange} />
      <div className={styles.tableCont}>
        <ReactTable
          data={disputes}
          columns={disputeColumns}
          onSelectRow={() => {}}
          selectedItems={[]}
          defaultSortingState={[]}
          setSortingState={() => {}}
          defaultStyling={{ header_height: 'header-height' }}
          disableColumnResizing={nonResizableColumns}
          defaultFeildSize={1.09}
          pageInfo={pageInfo}
          setPage={setPage}
          setPerPage={setPerPage}
          emptyIcon={EmptyDisputesIcon}
          emptyTitle={getEmptyTitle(t)}
          loading={disputesLoading}
          perPage={perPage}
          showTooltip
        />
      </div>
    </div>
  );
};

export default DisputesTable;
